/**
 * CalendarPnlHeatmap — a GitHub-style calendar of daily net P&L. Columns are
 * weeks, rows are weekdays; cell colour = green/red by P&L sign, intensity by
 * magnitude. Fills gaps (non-trading days) as blanks. Pure presentational —
 * driven by the `dailyPnl` the analytics summary already returns.
 */
import React, { useMemo } from 'react';
import { Box, Card, CardHeader, CardContent, Typography, Tooltip } from '@mui/material';
import { useAppTheme } from '../../hooks/useAppTheme';
import { formatCurrency } from '../../utils/formatters';

interface Props { dailyPnl: { date: string; pnl: number; trades: number }[] }

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const CELL = 13, GAP = 3;

const CalendarPnlHeatmap: React.FC<Props> = ({ dailyPnl }) => {
  const { C } = useAppTheme();

  const { weeks, maxAbs, totalDays } = useMemo(() => {
    const byDate = new Map<string, { pnl: number; trades: number }>();
    for (const d of dailyPnl) byDate.set(d.date, { pnl: d.pnl, trades: d.trades });
    if (!dailyPnl.length) return { weeks: [] as any[], maxAbs: 1, totalDays: 0 };
    const dates = dailyPnl.map(d => d.date).sort();
    const start = new Date(dates[0] + 'T00:00:00Z');
    const end = new Date(dates[dates.length - 1] + 'T00:00:00Z');
    // Align the grid start to the week's Sunday.
    const gridStart = new Date(start); gridStart.setUTCDate(gridStart.getUTCDate() - gridStart.getUTCDay());
    const weeks: Array<Array<{ key: string; pnl: number | null; trades: number; month: number; dom: number } | null>> = [];
    let cur = new Date(gridStart);
    let maxAbs = 1;
    for (const d of dailyPnl) maxAbs = Math.max(maxAbs, Math.abs(d.pnl));
    while (cur <= end) {
      const week: Array<any> = [];
      for (let i = 0; i < 7; i++) {
        const key = cur.toISOString().slice(0, 10);
        const hit = byDate.get(key);
        week.push({ key, pnl: hit ? hit.pnl : null, trades: hit ? hit.trades : 0, month: cur.getUTCMonth(), dom: cur.getUTCDate() });
        cur.setUTCDate(cur.getUTCDate() + 1);
      }
      weeks.push(week);
    }
    return { weeks, maxAbs, totalDays: byDate.size };
  }, [dailyPnl]);

  const cellColor = (pnl: number | null) => {
    if (pnl == null) return 'rgba(127,127,127,0.10)';
    if (pnl === 0) return 'rgba(127,127,127,0.18)';
    const t = 0.25 + 0.75 * Math.min(1, Math.abs(pnl) / maxAbs);
    const rgb = pnl > 0 ? '38,166,154' : '239,83,80';
    return `rgba(${rgb},${t.toFixed(2)})`;
  };

  if (!weeks.length) {
    return <Card sx={{ borderRadius: 1.5 }}><CardContent><Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>No daily P&amp;L in range yet.</Typography></CardContent></Card>;
  }

  // Month label per column where a new month first appears.
  const monthLabels = weeks.map((w: any[], i: number) => {
    const firstReal = w.find((c: any) => c && c.dom <= 7);
    if (!firstReal) return '';
    const prev = i > 0 ? weeks[i - 1].find((c: any) => c && c.dom <= 7) : null;
    return (!prev || prev.month !== firstReal.month) ? MON[firstReal.month] : '';
  });

  return (
    <Card sx={{ borderRadius: 1.5 }}>
      <CardHeader
        title={<Typography fontWeight={700}>Daily P&amp;L calendar</Typography>}
        subheader={<Typography variant="caption" color="text.secondary">{totalDays} trading days · green = up, red = down, intensity = size</Typography>}
        sx={{ pb: 0.5 }}
      />
      <CardContent>
        <Box sx={{ overflowX: 'auto', pb: 1 }}>
          <Box sx={{ display: 'inline-flex', gap: `${GAP}px`, alignItems: 'flex-start' }}>
            {/* weekday labels */}
            <Box sx={{ display: 'grid', gridTemplateRows: `16px repeat(7, ${CELL}px)`, gap: `${GAP}px`, mr: 0.5 }}>
              <Box />
              {DOW.map((d, i) => (
                <Typography key={d} sx={{ fontSize: '0.55rem', color: 'text.secondary', height: CELL, lineHeight: `${CELL}px`, visibility: i % 2 ? 'visible' : 'hidden' }}>{d}</Typography>
              ))}
            </Box>
            {/* week columns */}
            {weeks.map((w: any[], wi: number) => (
              <Box key={wi} sx={{ display: 'grid', gridTemplateRows: `16px repeat(7, ${CELL}px)`, gap: `${GAP}px` }}>
                <Typography sx={{ fontSize: '0.55rem', color: 'text.secondary', height: 16, lineHeight: '16px', whiteSpace: 'nowrap' }}>{monthLabels[wi]}</Typography>
                {w.map((c: any, ci: number) => c == null ? <Box key={ci} sx={{ width: CELL, height: CELL }} /> : (
                  <Tooltip key={ci} arrow title={c.pnl == null ? `${c.key} · no trades` : `${c.key} · ${formatCurrency(c.pnl)} · ${c.trades} trade${c.trades === 1 ? '' : 's'}`}>
                    <Box sx={{ width: CELL, height: CELL, borderRadius: '2px', bgcolor: cellColor(c.pnl), border: '1px solid rgba(127,127,127,0.12)' }} />
                  </Tooltip>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Loss</Typography>
          {[-1, -0.5, 0, 0.5, 1].map((v, i) => <Box key={i} sx={{ width: CELL, height: CELL, borderRadius: '2px', bgcolor: cellColor(v * maxAbs) }} />)}
          <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Profit</Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CalendarPnlHeatmap;
